import { useDroppable } from '@dnd-kit/core';
import { SEATING_SECTION_LABELS, type SeatingParty, type SeatingTable } from '@peles/shared';
import { PartyChip } from './PartyChip';
import { tableDropId } from './dropTargets';
import { emptySeats, partiesAt, tableTitle } from './boardModel';

function seatRange(seats: number[]): string {
  if (seats.length === 1) return `Seat ${seats[0]}`;
  const contiguous = seats.every((seat, i) => i === 0 || seat === seats[i - 1] + 1);
  return contiguous ? `Seats ${seats[0]}–${seats[seats.length - 1]}` : `Seats ${seats.join(', ')}`;
}

export interface TableCardProps {
  table: SeatingTable;
  parties: Map<string, SeatingParty>;
  /** A party is picked up: the card offers its empty seats, and says if it fits. */
  picking: { guestId: string; size: number } | null;
  selectedGuestId: string | null;
  proposed: Set<string>;
  highlighted: boolean;
  canWrite: boolean;
  onSeatHere: (seatNumber?: number) => void;
  onPartyClick: (guestId: string) => void;
  /** Meal counts at the table, when the plan is the one the guest list reads. */
  meals?: Record<string, number>;
}

/**
 * A table on the seating board: how full it is, the parties at it (in seat
 * order), and — while a party is picked up — whether it fits and which chairs
 * are free to put it on.
 */
export function TableCard({
  table,
  parties,
  picking,
  selectedGuestId,
  proposed,
  highlighted,
  canWrite,
  onSeatHere,
  onPartyClick,
  meals,
}: TableCardProps) {
  const { setNodeRef, isOver } = useDroppable({ id: tableDropId(table.uid), disabled: !canWrite });
  const seated = table.occupied.length;
  const over = seated > table.seats || table.occupied.some((s) => s.seatNumber > table.seats);
  const free = Math.max(0, table.seats - seated);
  const fits = picking ? free >= picking.size : false;
  const mealText = meals
    ? Object.entries(meals)
        .map(([meal, count]) => `${count} ${meal}`)
        .join(', ')
    : '';

  const ring = isOver
    ? 'border-primary-500 ring-2 ring-primary-200'
    : highlighted
      ? 'border-amber-400 ring-2 ring-amber-100'
      : picking && fits
        ? 'border-green-400'
        : 'border-gray-200';

  return (
    <section
      ref={setNodeRef}
      id={`seating-table-${table.uid}`}
      aria-label={tableTitle(table)}
      className={`rounded-2xl border bg-white p-3 ${ring}`}
      data-testid={`table-card-${table.uid}`}
    >
      <header className="flex items-center justify-between gap-2 mb-2">
        <button
          type="button"
          disabled={!picking || !canWrite}
          onClick={() => onSeatHere()}
          title={mealText || undefined}
          className="text-left text-sm font-semibold text-gray-900 disabled:cursor-default enabled:hover:underline"
        >
          {tableTitle(table)}
        </button>
        {table.section && (
          <span
            className={`ml-auto text-xs font-medium rounded-full px-2 py-0.5 ${
              table.section === 'MEN' ? 'bg-blue-50 text-blue-700' : 'bg-pink-50 text-pink-700'
            }`}
            data-testid={`table-section-${table.uid}`}
          >
            {table.section === 'MEN' ? 'Men' : 'Women'}
            <span className="sr-only"> — {SEATING_SECTION_LABELS[table.section]}</span>
          </span>
        )}
        <span
          className={`text-xs font-medium rounded-full px-2 py-0.5 ${
            over
              ? 'bg-red-100 text-red-700'
              : seated === table.seats
                ? 'bg-gray-100 text-gray-700'
                : 'bg-green-50 text-green-700'
          }`}
          data-testid={`table-occupancy-${table.uid}`}
        >
          {seated}/{table.seats}
        </span>
      </header>
      {over && (
        <p className="mb-2 text-xs text-red-700">Over by {Math.max(seated - table.seats, 1)}</p>
      )}
      <ul className="space-y-1.5">
        {partiesAt(table, parties).map((party) => (
          <li key={party.guestId}>
            <PartyChip
              guestId={party.guestId}
              where={table.uid}
              name={party.name}
              detail={seatRange(party.seats)}
              side={parties.get(party.guestId)?.side}
              pinned={party.locked}
              selected={selectedGuestId === party.guestId}
              proposed={proposed.has(party.guestId)}
              draggable={canWrite && !party.locked}
              onClick={() => onPartyClick(party.guestId)}
            />
          </li>
        ))}
      </ul>
      {picking && canWrite && free > 0 && (
        <div
          className="mt-2 flex flex-wrap gap-1"
          aria-label={`Empty seats at ${tableTitle(table)}`}
        >
          {emptySeats(table).map((seat) => (
            <button
              key={seat}
              type="button"
              onClick={() => onSeatHere(seat)}
              aria-label={`Seat ${seat} at ${tableTitle(table)}`}
              className="h-7 w-7 rounded-full border border-dashed border-gray-400 text-xs text-gray-600 hover:border-primary-500 hover:text-primary-700"
            >
              {seat}
            </button>
          ))}
        </div>
      )}
      {!picking && free > 0 && seated > 0 && (
        <p className="mt-2 text-xs text-gray-500">
          {free} empty {free === 1 ? 'seat' : 'seats'}
        </p>
      )}
      {seated === 0 && !picking && (
        <p className="text-xs text-gray-500">Empty — {table.seats} seats</p>
      )}
    </section>
  );
}
